import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Icon from "../../components/icons";
import InfoModal from "./infoModal";
import toast from "react-hot-toast";
import { URLS } from "../../_config";
import { ThirdPartyDatabaseNewProps } from "../../_config/interfaces/interfaces";
import { useDataSourceApi } from "../../store/api/hooks/dataSourceApiHook";
import { de } from "date-fns/locale";

interface FormSubmitValues {
  storeType: { value: string; label: string };
  url: string;
}

const ThirdPartyDatabase: React.FC<ThirdPartyDatabaseNewProps> = ({
  branchDS,
  setOnSubmitLoading,
  isAddBtnDisabled,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<any>();
  const navigate = useNavigate();
  const dataSourceAPI = useDataSourceApi();

  const onSubmit = (data: FormSubmitValues) => {
    console.log('inputGroups =>', data);
    switch (data.storeType?.value) {
      case "yelp":
        if (!data.url.includes("yelp.com")) {
          toast.error("Please enter a valid Yelp URL");
          return;
        }
        break;
      case "google":
        if (!data.url.includes("google.com")) {
          toast.error("Please enter a valid Google URL");
          return;
        }
        break;
      default:
        return true;
    }
    setOnSubmitLoading(true);
    const param = {
      store_id: branchDS?.store_id,
      branch_id: branchDS?.branch_id,
      source_type: data.storeType?.value,
      url: data.url,
    };

    setTimeout(() => {
      dataSourceAPI.addExternalDataSource(
        param,
        (msg: any, resp: any) => {
          // console.log('response =>', resp);
          if (resp?.status === false && resp?.code === 200) {
            toast(resp?.message, { icon: '⚠️' });
          } else if (resp?.code === 201 || resp?.code === 200) {
            toast.success(resp?.message);
            navigate(URLS.DATASOURCES.LIST);
            // navigate(URLS.DASHBOARD);
          } else {
            // toast.error(resp?.message);
          }
          setOnSubmitLoading(false);
        },
        (error: any, resp: any) => {
          // console.log('error =>', error, resp);
          if (resp?.detail?.code === 400) {
            // toast.error(resp?.detail?.message);
            navigate(URLS.DATASOURCES.LIST);
            // navigate(URLS.DASHBOARD);
          } else {
            toast.error("Something went wrong");
          }
          setOnSubmitLoading(false);
        }
      );
    }, 5000);
  };

  const onClickInfoModal = () => {
    setIsModalOpen(true);
  };

  // Function to handle modal closing
  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col external-database-container bg-sky-50">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex">
          <div className="p-8 w-full mobile-padding-0">
            <div className="third_party_database">
              <div className="input-container">
                {/* Gray box for input fields */}
                <div className="bg-gray-200 shadow-md rounded-md p-6 max-w-5xl">
                  <div className="input-group flex items-center mb-4 space-x-4 mobile-column mobile-justify-start">
                    {/* Source (Select) */}
                    <div className="flex-shrink-0 w-64 mobile-full-width">
                      <label
                        htmlFor="storeType"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Source
                      </label>
                      <div className="mt-2">
                        <Controller
                          name="storeType"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={[{ value: 'yelp', label: 'Yelp' }]}
                              placeholder="Source"
                              isSearchable={false}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderRadius: '2px',
                                  borderColor: errors.storeType ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                                  borderWidth: '1px',
                                  boxShadow: 'none',
                                  '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#d1d5db' },
                                  minHeight: '36px',
                                  fontSize: '14px',
                                  backgroundColor: 'white',
                                }),
                                placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '14px' }),
                                option: (base, state) => ({
                                  ...base,
                                  color: state.isSelected ? 'white' : '#6b7280',
                                  backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                                  fontSize: '14px',
                                }),
                                singleValue: (base) => ({ ...base, color: '#111827', fontSize: '14px' }),
                              }}
                            />
                          )}
                        />
                        <InfoModal isOpen={isModalOpen} onClose={closeModal} />
                      </div>
                    </div>

                    {/* URL (Input) */}
                    <div className="flex-1 ml-4 mt-2 relative mobile-full-width mobile-ml-0">
                      <label
                        htmlFor="url"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Url
                      </label>
                      <Icon
                        name="info"
                        size={20}
                        className="absolute left-6 top-[8px] transform -translate-y-1/2 text-gray-400 pointer-events-auto cursor-pointer"
                        tooltipText="Enter full URL of the review page"
                        tooltipRequired={true}
                        // onClick={() => {
                        //   onClickInfoModal();
                        // }}
                      />
                      <input
                        id="url"
                        type="text"
                        {...register("url", {
                          required: true,
                          validate: (value) => {
                            try {
                              // Attempt to create a URL object which will throw an error if invalid
                              new URL(value);
                              return true;
                            } catch {
                              return "Please enter a valid URL";
                            }
                          },
                        })}
                        className={`block-lg w-full rounded-sm bg-white px-3 py-1.5 pr-10 text-sm text-gray-900 outline outline-1 -outline-offset-1 ${
                          errors.url
                            ? "outline-red-500 outline-2"
                            : "outline-gray-300"
                        } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-start ml-4 mt-2 mobile-ml-0">
          <button
            className="flex justify-center w-32 ml-4 rounded-sm bg-blue-500 p-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 mobile-ml-0 disabled:bg-blue-200"
            type="submit"
            disabled={/*isAddBtnDisabled*/false}
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default ThirdPartyDatabase;
